import CommonRouteTabs from '@/components/common/CommonRouteTabs'
import DashboardFilter from '@/components/dashboard/DashboardFilter'
import Layout from '@/components/layout/Layout'
import { ROUTES } from '@/routes/routeConfig'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import { PREPARER, IT_ADMIN } from '@/utils/role-capabilities.js'
import { getDashboardLastSyncedDate } from '@/api/dashboardApi'

const tabs = [
  {
    id: 'overview',
    displayName: 'Dashboard',
    route: `${ROUTES.DASHBOARD}/${ROUTES.DASHBOARD_OVERVIEW}`,
  },
  {
    id: 'compliance',
    displayName: 'Compliances',
    route: `${ROUTES.DASHBOARD}/${ROUTES.DASHBOARD_COMPLIANCE}`,
  },
]

const Dashboard = () => {
  const [filterOpen, setFilterOpen] = useState(false)
  // Formatted last-synced timestamp (DD-MMM-YYYY HH:mm) from the API.
  const [lastSynced, setLastSynced] = useState('')
  const filterAnchorRef = useRef(null)
  const userRole = useSelector(state => state.user.role)
  const location = useLocation()
  // Last synced is only relevant on the dashboard overview route.
  const isOverviewRoute = location.pathname.endsWith(ROUTES.DASHBOARD_OVERVIEW)
  // On the Compliances tab the Currency and Value conversion filters don't apply,
  // so disable those dropdowns in the filter popover.
  const isComplianceRoute = location.pathname.endsWith(
    ROUTES.DASHBOARD_COMPLIANCE
  )

  useEffect(() => {
    const fetchLastSynced = async () => {
      const response = await getDashboardLastSyncedDate()
      const isoDate = response?.data?.data?.lastSyncedDate
      if (response?.success && isoDate) {
        setLastSynced(dayjs(isoDate).format('DD-MMM-YYYY HH:mm'))
      }
    }
    fetchLastSynced()
  }, [])

  const handleFilterToggle = () => {
    setFilterOpen(prev => !prev)
  }

  const handleFilterClose = () => {
    setFilterOpen(false)
  }

  if (userRole === PREPARER || userRole === IT_ADMIN) {
    return (
      <Layout
        sx={{
          positive: 'relative',
        }}
      >
        {/* Filter Toggle Bar */}
        <Box
          ref={filterAnchorRef}
          onClick={handleFilterToggle}
          sx={{
            width: '100%',
            height: '64px',
            borderBottom: filterOpen ? 'none' : '1px solid #e0e0e0',
            backgroundColor: '#fff',
            boxShadow: filterOpen ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
            position: 'sticky',
            top: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            py: 1.5,
            px: 2,
            userSelect: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--forbrand-primary)',
            }}
          >
            Filters
          </Typography>
          {filterOpen ? (
            <KeyboardArrowUp
              sx={{ fontSize: 20, color: 'var(--forbrand-primary)' }}
            />
          ) : (
            <KeyboardArrowDown
              sx={{ fontSize: 20, color: 'var(--forbrand-primary)' }}
            />
          )}
        </Box>

        {/* Filter Popper */}
        <DashboardFilter
          open={filterOpen}
          disableCurrencyAndConversion={isComplianceRoute}
          anchorEl={filterAnchorRef.current}
          onClose={handleFilterClose}
        />

        {/* Tabs with Last Synced */}
        <Box
          sx={{
            my: 2,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CommonRouteTabs tabs={tabs} />
          </Box>
          {isOverviewRoute && lastSynced && (
            <Typography
              sx={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 400,
                color: 'var(--forbrand-secondary, #626771)',
                whiteSpace: 'nowrap',
                pb: 1.5,
                borderBottom: '1px solid var(--border-strong, #8E95A2)',
              }}
            >
              Last synced: {lastSynced}
            </Typography>
          )}
        </Box>

        {/* Tab Content rendered by child routes */}
        <Outlet />
      </Layout>
    )
  } else {
    return (
      <Layout>
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Dashboard</h1>
          <p className="text-gray-600">
            Welcome to the PWC Withholding Tax Dashboard
          </p>
        </div>
      </Layout>
    )
  }
}

export default Dashboard
